/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import type {
  ApiListResponse,
  DocumentActe,
} from '@mairistem/administres-utils';

import { TABLE_ACTE_BY_TYPE_ACTE, URL_DOCUMENTS } from '@/constants';
import type { TypeActe } from '@/types';
import { fileToBase64 } from '@/utils/documentPreview';

import type { NouveauDocument } from '@/components/Acte/Documents/ModalAjouterDocument';

/**
 * Documents rattachés à un acte.
 *
 * Le filtrage se fait sur le **couple** `identifiantActe` + `tableActe` : deux
 * actes de types différents peuvent porter le même identifiant numérique, et
 * l'index de l'API ne le déduit pas tout seul.
 *
 * L'image de l'acte (`acte: true`) et les autres pièces du dossier
 * (justificatifs…) sont renvoyées ensemble, l'image d'abord — c'est l'ordre
 * d'affichage attendu par la liste.
 */
const useDocumentsActe = (identifiantActe?: number, typeActe?: TypeActe) => {
  const [documents, setDocuments] = React.useState<Array<DocumentActe>>([]);
  const [loading, setLoading] = React.useState(false);

  const tableActe = typeActe ? TABLE_ACTE_BY_TYPE_ACTE[typeActe] : undefined;

  const refresh = React.useCallback(() => {
    if (!identifiantActe || !tableActe) return Promise.resolve([]);

    setLoading(true);

    const params = new URLSearchParams({
      identifiantActe: String(identifiantActe),
      tableActe,
      perPage: '100',
    });

    return request
      .get<unknown, ApiListResponse<DocumentActe>>(
        `${URL_DOCUMENTS}?${params.toString()}`,
      )
      .then(({ data }) => {
        // L'image de l'acte en tête, les pièces annexes ensuite.
        const tries = _.orderBy(data ?? [], [(doc) => !doc.acte], ['asc']);
        setDocuments(tries);
        return tries;
      })
      .catch(() => {
        toast('Erreur lors du chargement des documents', { type: 'error' });
        setDocuments([]);
        return [];
      })
      .finally(() => {
        setLoading(false);
      });
  }, [identifiantActe, tableActe]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Ajoute un document au dossier de l'acte, avec son contenu.
   *
   * Le binaire vit dans `ec_pieces.pi_bin` : `POST /documents` accepte
   * désormais un bloc `fichier: { contenu, nom }` optionnel, sur le même
   * contrat que les endpoints de création d'acte numérisé. Sans lui, la
   * création ne produirait qu'un document sans contenu.
   */
  const ajouter = React.useCallback(
    async (nouveau: NouveauDocument): Promise<DocumentActe | null> => {
      if (!identifiantActe || !tableActe) return null;

      setLoading(true);

      try {
        const contenu = await fileToBase64(nouveau.fichier);

        const cree = await request.post<unknown, DocumentActe>(URL_DOCUMENTS, {
          identifiantActe,
          tableActe,
          nom: nouveau.nom,
          type: nouveau.type,
          acte: nouveau.acte,
          // `ec_documents` n'a pas de colonne de description : elle est portée
          // par `dc_zsaisie`.
          zoneSaisie: nouveau.description || undefined,
          fichier: { contenu, nom: nouveau.fichier.name },
        });

        await refresh();

        return cree;
      } catch {
        toast("Erreur lors de l'ajout du document", { type: 'error' });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [identifiantActe, tableActe, refresh],
  );

  /**
   * Modifie le nom et la description d'un document déjà déposé.
   *
   * La description est portée par `dc_zsaisie` ; `null` l'efface, là où une
   * chaîne vide serait refusée par la validation `string` de l'API.
   */
  const modifier = React.useCallback(
    (identifiant: number, modification: { nom: string; description: string }) =>
      request
        .put(`${URL_DOCUMENTS}/${identifiant}`, {
          nom: modification.nom,
          zoneSaisie: modification.description || null,
        })
        .then(() => refresh())
        .catch(() => {
          toast('Erreur lors de la modification du document', {
            type: 'error',
          });
        }),
    [refresh],
  );

  const supprimer = React.useCallback(
    (identifiant: number) =>
      request
        .delete(`${URL_DOCUMENTS}/${identifiant}`)
        .then(() => refresh())
        .catch(() => {
          toast('Erreur lors de la suppression du document', { type: 'error' });
        }),
    [refresh],
  );

  return { documents, loading, refresh, ajouter, modifier, supprimer };
};

export default useDocumentsActe;
